import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import numbro from 'numbro';
import { GoalComponentProps as Props } from '../../../../components/Goal/Goal.meta';
import {
  EstatePlanningGoalDataDTO,
  FORM_ID,
  FormValues,
  UPSERT_GOAL_URL,
} from '../../EstatePlanning.meta';
import { useGoalsQuery } from '../../../../api/useGoalsQuery';
import { useUpsertGoalMutation } from '../../../../api/useUpsertGoalMutation';
import { UpsertGoalModal } from '../../../../components/UpsertGoalModal/UpsertGoalModal';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { HelperText } from '../../../../../../CommonComponents/OPL/HelperText/HelperText';
import { formatCurrency } from '../../../../../../CommonUtils/formatNumber';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Separator } from '../../../../../../CommonComponents/OPL/Separator/Separator';
import { AssetBucketingSelect } from '../../../../components/AssetBucketingSelect/AssetBucketingSelect';

export const EstatePlanningForm = (props: Props<EstatePlanningGoalDataDTO>) => {
  const {
    clientId,
    onClose,
    assetBucketingIndices,
    goalData: {
      hasSpouse,
      principalTaxFilingState,
      estatePlanningDetails: { spouseTaxFilingState, deductionsAndGifts },
    },
    ...rest
  } = props;

  const { data } = useGoalsQuery({ clientId });

  if (!data) {
    return null;
  }

  const {
    estatePlanningOptions,
    filingStateOptions,
    assetBucketingOptions: { enableGoalAssetBucketing },
  } = data;

  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      assetBucketingIndices,
      principalTaxFilingState,
      spouseTaxFilingState: spouseTaxFilingState ?? principalTaxFilingState,
      deductionsAndGifts,
    },
  });

  const {
    mutateAsync: upsertGoal,
    isLoading: isUpsertingGoal,
    error: upsertGoalError,
  } = useUpsertGoalMutation({ clientId, url: UPSERT_GOAL_URL });

  const principalLifetimeGiftingExemptionUsedAmount = watch(
    'deductionsAndGifts.principalDeductionsAndGifts.lifetimeGiftingExemptionUsedAmount',
  );
  const spouseLifetimeGiftingExemptionUsedAmount = watch(
    'deductionsAndGifts.spouseDeductionsAndGifts.lifetimeGiftingExemptionUsedAmount',
  );

  const { addUnusedExemption } = estatePlanningOptions;

  const onSubmit = async (values: FormValues) => {
    try {
      await upsertGoal(values);

      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const stateOptions = filingStateOptions.filter(({ isInsideUS }) => isInsideUS);

  const primaryEstateOptions = estatePlanningOptions.primaryEstateOptions;
  // Math.max Used to prevent negative values. If the calculation result is negative it will return 0
  const principalRemainingAmount = Math.max(
    0,
    primaryEstateOptions
      ? numbro(primaryEstateOptions.baseLifetimeGiftingExemptionAmount)
          .subtract(principalLifetimeGiftingExemptionUsedAmount || 0)
          .value() || 0
      : 0,
  );

  return (
    <UpsertGoalModal
      clientId={clientId}
      formId={FORM_ID}
      isLoading={isUpsertingGoal}
      onClose={onClose}
      {...rest}
    >
      {upsertGoalError && <Alert message={upsertGoalError.message} variant="error" mb="16" />}

      <Box
        id={FORM_ID}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        aria-label="Estate Planning goal form"
        display="flex"
        flexDirection="column"
        css="gap: 16px"
      >
        <Controller
          control={control}
          name="deductionsAndGifts.principalDeductionsAndGifts.administrationExpenseDeductionAmount"
          render={({ field }) => (
            <InputNumberMask
              {...field}
              leftIcon="fa fa-dollar-sign"
              label="Estate Administration Expense Deduction"
              allowNegative={false}
              tooltip="Enter your estate administration expenses. This amount will be deducted from the gross estate."
            />
          )}
        />

        <Box>
          <Controller
            control={control}
            name="deductionsAndGifts.principalDeductionsAndGifts.lifetimeGiftingExemptionUsedAmount"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                leftIcon="fa fa-dollar-sign"
                label="Lifetime Gifting Exemption Used Amount"
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                allowNegative={false}
              />
            )}
          />

          {primaryEstateOptions && (
            <HelperText mt="4">
              Remaining amount:{' '}
              {formatCurrency(principalRemainingAmount, {
                decimalPlaces: 0,
              })}
            </HelperText>
          )}
        </Box>

        <Controller
          control={control}
          name="principalTaxFilingState"
          render={({ field: { value, onChange, ref } }) => (
            <Select
              innerRef={ref}
              value={stateOptions.find(option => option.value === value)}
              label="State of Passing"
              options={stateOptions}
              onChange={opt => onChange(opt ? opt.value : '')}
              isPortal
            />
          )}
        />

        {hasSpouse && (
          <>
            <Separator mt="8" />

            <Text fontWeight={500} fontSize="h6" color="neutral.600" mt="16">
              Your Partner
            </Text>

            <Controller
              control={control}
              name="deductionsAndGifts.spouseDeductionsAndGifts.administrationExpenseDeductionAmount"
              render={({ field }) => (
                <InputNumberMask
                  {...field}
                  leftIcon="fa fa-dollar-sign"
                  label="Estate Administration Expense Deduction"
                  allowNegative={false}
                />
              )}
            />

            {estatePlanningOptions.spouseEstateOptions && (
              <Box>
                <Controller
                  control={control}
                  name="deductionsAndGifts.spouseDeductionsAndGifts.lifetimeGiftingExemptionUsedAmount"
                  render={({ field: { onChange, ...field } }) => (
                    <InputNumberMask
                      {...field}
                      onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                      leftIcon="fa fa-dollar-sign"
                      label="Lifetime Gifting Exemption Used Amount"
                      allowNegative={false}
                    />
                  )}
                />

                <HelperText mt="4">
                  Remaining amount:{' '}
                  {formatCurrency(
                    // Math.max Used to prevent negative values. If the calculation result is negative it will return 0
                    Math.max(
                      0,
                      numbro(
                        estatePlanningOptions.spouseEstateOptions
                          .baseLifetimeGiftingExemptionAmount,
                      )
                        .add(addUnusedExemption ? principalRemainingAmount : 0)
                        .subtract(spouseLifetimeGiftingExemptionUsedAmount || 0)
                        .value() || 0,
                    ),
                    {
                      decimalPlaces: 0,
                    },
                  )}
                </HelperText>
              </Box>
            )}

            <Controller
              control={control}
              name="spouseTaxFilingState"
              render={({ field: { value, onChange, ref } }) => (
                <Select
                  innerRef={ref}
                  label="State of Passing"
                  options={stateOptions}
                  value={stateOptions.find(option => option.value === value)}
                  onChange={opt => onChange(opt ? opt.value : '')}
                  isPortal
                />
              )}
            />
          </>
        )}

        {enableGoalAssetBucketing && (
          <>
            <Separator mt="8" />

            <AssetBucketingSelect clientId={clientId} control={control} />
          </>
        )}
      </Box>
    </UpsertGoalModal>
  );
};
